import { AdminApiError, callAdmin } from './api';
import { endOtherCellSessions } from './cell-clients';
import { supabase } from './supabase';

/** The client a sign-in step runs on: this tab's cell unless told otherwise. */
export type AuthClient = Pick<typeof supabase, 'auth'>;

/**
 * Staff sign-in: either email and password then the authenticator app (TOTP,
 * enrolled on the first sign-in), or a passkey on its own. A passkey is device
 * plus face, fingerprint or PIN, so it is a complete sign-in (Supabase calls
 * it passwordless, aal1, method `passkey`); admin-api and the database accept
 * such a session as strong on their own. Hosted Supabase does not enable
 * WebAuthn as an MFA factor, so a passkey is never offered as a second step.
 */

export type SignInStage =
  | { stage: 'signed_out' }
  | { stage: 'enrol' }
  | { stage: 'verify'; factorId: string }
  | { stage: 'done' };

/**
 * What the second-factor box hands back in place of six digits when the
 * person chose their passkey.
 */
export const PASSKEY_CHOICE = 'passkey';

/** Whether this session was signed in with a passkey. */
async function signedInWithPasskey(
  client: AuthClient = supabase,
): Promise<boolean> {
  const { data } = await client.auth.mfa.getAuthenticatorAssuranceLevel();
  return (
    data?.currentAuthenticationMethods?.some(
      (entry) => typeof entry !== 'string' && entry.method === 'passkey',
    ) ?? false
  );
}

export async function currentStage(
  client: AuthClient = supabase,
): Promise<SignInStage> {
  const { data: session } = await client.auth.getSession();
  if (!session.session) return { stage: 'signed_out' };

  const { data: level } =
    await client.auth.mfa.getAuthenticatorAssuranceLevel();
  if (level?.currentLevel === 'aal2' || (await signedInWithPasskey(client))) {
    return { stage: 'done' };
  }

  const { data: factors } = await client.auth.mfa.listFactors();
  const verified = factors?.totp.find((factor) => factor.status === 'verified');
  return verified
    ? { stage: 'verify', factorId: verified.id }
    : { stage: 'enrol' };
}

export async function signInWithPassword(
  email: string,
  password: string,
  client: AuthClient = supabase,
): Promise<SignInStage> {
  const { error } = await client.auth.signInWithPassword({
    email: email.trim(),
    password,
  });
  if (error)
    throw new Error('That email and password did not match a staff account.');
  return currentStage(client);
}

export interface Passkey {
  id: string;
  name: string;
  createdAt: string | null;
  lastUsedAt: string | null;
  /** Confirmed with an authenticator code: only then can it sign anyone in. */
  confirmed: boolean;
}

/** The signed-in person's passkeys, each with whether it was confirmed. */
export async function listPasskeys(): Promise<Passkey[]> {
  const { data, error } = await supabase.auth.passkey.list();
  if (error) throw new Error('Could not load your passkeys. Try again.');
  const { confirmed } = await callAdmin('passkeys.mine', {});
  return (data ?? []).map((passkey) => ({
    id: passkey.id,
    name: passkey.friendly_name || 'Passkey',
    createdAt: passkey.created_at ?? null,
    lastUsedAt: passkey.last_used_at ?? null,
    confirmed: confirmed.includes(passkey.id),
  }));
}

/** Whether the signed-in person has a confirmed passkey to offer for the fresh-proof step. */
export async function hasPasskey(): Promise<boolean> {
  try {
    return (await listPasskeys()).some((passkey) => passkey.confirmed);
  } catch {
    return false;
  }
}

/**
 * Confirms a passkey with a fresh authenticator code. A passkey counts only
 * once its owner did: anyone holding a signed-in session can register one, so
 * an unconfirmed passkey never signs anyone in.
 */
export async function confirmPasskey(
  passkeyId: string,
  code: string,
): Promise<void> {
  await confirmSecondFactor(code);
  try {
    await callAdmin('passkeys.confirm', { passkey_id: passkeyId });
  } catch (error) {
    if (error instanceof AdminApiError && error.code === 'MFA_STALE') {
      throw new Error('That code is too old. Enter a new one.');
    }
    throw error;
  }
}

/**
 * Registers a passkey for the signed-in person (the browser asks for Face ID,
 * Touch ID, Windows Hello, a PIN or a security key) and confirms it with the
 * authenticator code. If the confirmation fails the passkey stays unconfirmed
 * and is shown as such, so it can be confirmed or removed.
 */
export async function addPasskey(name: string, code: string): Promise<void> {
  await confirmSecondFactor(code);
  const { data, error } = await supabase.auth.registerPasskey();
  if (error) throw new Error(passkeyMessage(error));
  const friendlyName = name.trim();
  if (friendlyName && data?.id) {
    await supabase.auth.passkey.update({
      passkeyId: data.id,
      friendlyName: friendlyName.slice(0, 120),
    });
  }
  if (!data?.id) throw new Error('The passkey could not be saved. Try again.');
  await callAdmin('passkeys.confirm', { passkey_id: data.id });
}

export async function removePasskey(passkeyId: string): Promise<void> {
  const { error } = await supabase.auth.passkey.delete({ passkeyId });
  if (error) throw new Error('Could not remove the passkey. Try again.');
}

/** The words shown when admin-api turns a passkey session away. */
const PASSKEY_REFUSED =
  'This passkey cannot sign you in yet: it is not confirmed, or another passkey on your account is not. Sign in with your password and authenticator code, then confirm it on My security.';

/**
 * Whether admin-api accepts this session. A passkey the owner never confirmed
 * is refused there, and the browser cannot tell: ask once, and sign out when
 * it is turned away.
 */
async function acceptedByStaffPlane(): Promise<void> {
  try {
    await callAdmin('session.me', {});
  } catch (error) {
    if (error instanceof AdminApiError && error.code === 'MFA_REQUIRED') {
      await supabase.auth.signOut({ scope: 'local' });
      throw new Error(PASSKEY_REFUSED);
    }
    throw error;
  }
}

/** Signs in with a passkey, with no email or password asked. */
export async function signInWithPasskey(): Promise<SignInStage> {
  const { error } = await supabase.auth.signInWithPasskey();
  if (error) throw new Error(passkeyMessage(error));
  await acceptedByStaffPlane();
  return currentStage();
}

/**
 * Proves the passkey again before an approval or a change: a fresh passkey
 * sign-in, which admin-api accepts for five minutes. It must be the same
 * person's: a passkey of someone else on this device is refused and signs out.
 */
export async function verifyWithPasskey(): Promise<void> {
  const { data: before } = await supabase.auth.getUser();
  const { data, error } = await supabase.auth.signInWithPasskey();
  if (error) throw new Error(passkeyMessage(error));
  if (before.user && data?.user && data.user.id !== before.user.id) {
    await supabase.auth.signOut({ scope: 'local' });
    throw new Error('That passkey belongs to someone else. Sign in again.');
  }
  await acceptedByStaffPlane();
}

function passkeyMessage(error: { name?: string; message?: string }): string {
  if (
    error.name === 'WebAuthnError' ||
    /not allowed|cancel|abort/i.test(error.message ?? '')
  ) {
    return 'The passkey was not confirmed. Try again.';
  }
  return 'The passkey could not be used. Try again, or use your password and authenticator code.';
}

export interface Enrolment {
  factorId: string;
  /** An SVG data URI to scan. */
  qrCode: string;
  /** The same secret, to type in by hand. */
  secret: string;
}

let enrolling: Promise<Enrolment> | null = null;

/**
 * One setup at a time: StrictMode mounts the enrol step twice in dev, and two
 * concurrent enrols collide on the factor's friendly name. The second caller
 * shares the first one's result.
 */
export function enrolAuthenticator(): Promise<Enrolment> {
  enrolling ??= startEnrolment().finally(() => {
    enrolling = null;
  });
  return enrolling;
}

async function startEnrolment(): Promise<Enrolment> {
  // A factor left unverified by an earlier, abandoned attempt blocks a new one.
  const { data: factors } = await supabase.auth.mfa.listFactors();
  await Promise.all(
    (factors?.all ?? [])
      .filter((factor) => factor.status !== 'verified')
      .map((factor) => supabase.auth.mfa.unenroll({ factorId: factor.id })),
  );

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: `Sao staff ${new Date().toISOString().slice(0, 10)}`,
    issuer: 'Sao Staff',
  });
  if (error || !data)
    throw new Error('Could not start the authenticator setup. Try again.');
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  };
}

export async function verifyCode(
  factorId: string,
  code: string,
  client: AuthClient = supabase,
): Promise<void> {
  const { error } = await client.auth.mfa.challengeAndVerify({
    factorId,
    code: code.replace(/\s/g, ''),
  });
  if (error)
    throw new Error('That code did not match. Check the app and try again.');
}

/**
 * Proves the second factor again before an approval, by code or by passkey
 * (the code is `PASSKEY_CHOICE`): admin-api refuses an approval whose last
 * proof is older than five minutes.
 */
export async function confirmSecondFactor(code: string): Promise<void> {
  if (code === PASSKEY_CHOICE) return verifyWithPasskey();
  const { data: factors } = await supabase.auth.mfa.listFactors();
  const factor = factors?.totp.find((item) => item.status === 'verified');
  if (!factor) throw new Error('No authenticator is set up for this account.');
  await verifyCode(factor.id, code);
}

export async function signOut(): Promise<void> {
  // Sessions this tab opened in other cells end with this one.
  await endOtherCellSessions();
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session) await callAdmin('session.sign_out', {});
  } catch {
    // The session may already be over; signing out locally is what matters.
  }
  await supabase.auth.signOut({ scope: 'local' });
}
